import { FieldInputTypeName, FieldInputTypes, Preset, Template } from '@ekkogmbh/apisdk';
import { Avatar, Button, Chip, Grid, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { PresetPanelSorting } from './PresetPanelSorting';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { Add } from '@mui/icons-material';
import { TemplatePickerDialog } from 'src/TemplateManagement/Components/TemplatePickerDialog';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import usePagination from 'src/Common/Providers/PaginationProvider';

const overlineStyle = { fontWeight: 700 };

interface Props {
  preset?: Preset;
  closeHandler: () => void;
}

export const PresetPanel = ({ preset, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { setStep, enableSave, enableNext, currentStep } = usePanel();
  const { refresh } = usePagination();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [keys, setKeys] = useState<string[]>([]);
  const [types, setTypes] = useState<FieldInputTypes>({});

  const [keyInput, setKeyInput] = useState<string>('');
  const [isPickingTemplate, setPickingTemplate] = useState<boolean>(false);
  const isAllFilled = useMemo(() => ![name, coordinate].includes('') && keys.length > 0, [name, coordinate, keys]);
  const isEditMode = useMemo(() => preset !== undefined, [preset]);
  const initialStep = useMemo(() => (preset === undefined ? 0 : 1), [preset]);

  const [isSaving, startSaving] = useRequest<Preset>();

  const reset = useCallback(() => {
    setKeyInput('');
    setName(preset?.name ?? '');
    setCoordinate(preset?.coordinatePrefix.replace(/[_\-.]$/, '') ?? '');
    setKeys(preset?.keys ?? []);
    setTypes(preset?.types ?? {});

    setStep(initialStep);
  }, [preset, initialStep]);

  const savePreset = useCallback(async () => {
    startSaving(
      async () => await api.savePreset({ name, coordinate, keys, types }),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [isAllFilled, name, coordinate, keys, types, closeHandler]);

  const onAddKey = useCallback(() => {
    if (keyInput.length > 0 && !keys.includes(keyInput)) {
      const newKeys = Array.from(keys);
      newKeys.push(keyInput);
      setKeys(newKeys);
      setTypes({ ...types, [keyInput]: { name: FieldInputTypeName.ANY } });
      setKeyInput('');
    }
  }, [keyInput, keys, types]);

  const onAddTemplateKeys = useCallback(
    (template: Template) => {
      const addedKeys = template.fields.filter((candidate: string) => !keys.includes(candidate));
      const newKeys = keys.concat(addedKeys);
      const newTypes: FieldInputTypes = {};
      newKeys.forEach((key: string) => (newTypes[key] = { name: FieldInputTypeName.ANY }));

      setKeys(newKeys);
      setTypes(newTypes);
    },
    [keys, types],
  );

  useEffect(() => {
    reset();
  }, [preset]);

  useEffect(() => {
    enableSave(isAllFilled);

    switch (currentStep) {
      case 0:
        enableNext(![name, coordinate].includes(''));
        break;
      case 1:
        enableNext(keys.length > 0);
        break;
    }
  }, [currentStep, isAllFilled, name, coordinate, keys]);

  const idStep = useMemo(
    () => ({
      title: 'Preset',
      description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            type={'text'}
            label={'Name'}
            value={name}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
            disabled={isEditMode}
          />
          <CoordinateInput
            value={coordinate}
            onChange={(coordinate) => {
              setCoordinate(coordinate);
            }}
            disabled={isEditMode}
            trailingDelimiter={false}
          />
        </ReactivePanelColumn>
      ),
    }),
    [isEditMode, name, coordinate],
  );

  const fieldsStep = useMemo(
    () => ({
      title: 'Fields',
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'60%'}>
          <StyledTextField
            label={'Field Key'}
            value={keyInput}
            type={'text'}
            onChange={(e) => setKeyInput(String(e.target.value))}
            onKeyDown={(e) => {
              switch (e.key) {
                case 'Enter':
                  onAddKey();
                  break;
                case 'Escape':
                  setKeyInput('');
                  break;
              }
            }}
            action={{
              icon: Add,
              onClick: () => onAddKey(),
              disabled: keys.includes(keyInput) || keyInput.trim() === '',
            }}
          />
          <Button
            color={'primary'}
            fullWidth
            variant={'outlined'}
            onClick={() => setPickingTemplate(true)}
            sx={{
              alignSelf: 'center',
            }}
          >
            {'Import from Template'}
          </Button>
          <PresetPanelSorting
            preset={{ keys, types }}
            onChangeKeys={(k) => setKeys(k)}
            onChangeTypes={(t) => setTypes(t)}
          />
        </ReactivePanelColumn>
      ),
    }),
    [keyInput, onAddKey],
  );

  const overviewStep = useMemo(
    () => ({
      title: 'Overview',
      contentCallback: () => (
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'overline'} color={'primary'} style={overlineStyle}>
              {'Name'}
            </Typography>
            <Typography variant={'h6'} gutterBottom>
              {name}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'overline'} color={'primary'} style={overlineStyle}>
              {'Coordinate'}
            </Typography>
            <Typography variant={'h6'}>{coordinate}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'overline'} color={'primary'} style={overlineStyle}>
              {'Fields'}
            </Typography>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            {keys.map((k, i) => {
              const fieldType = types[k];
              const typeString: string = fieldType ? fieldType.name : '?';

              return (
                <Grid key={`preset-overview-key-${i}`} item xs={12} sm={6} md={3} lg={2}>
                  <Tooltip arrow title={'type: ' + typeString} placement={'top'}>
                    <Chip
                      variant={'outlined'}
                      avatar={<Avatar>{typeString[0].toLocaleUpperCase()}</Avatar>}
                      label={k}
                    />
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ),
    }),
    [name, coordinate, types, keys],
  );

  return (
    <>
      {isPickingTemplate && (
        <TemplatePickerDialog
          title={'Import Preset Field Keys From Template'}
          coordinate={coordinate}
          onClose={() => setPickingTemplate(false)}
          onConfirmCallback={(template) => {
            onAddTemplateKeys(template);
            setPickingTemplate(false);
          }}
        />
      )}
      <ContentPanel
        isLoading={isSaving}
        initialStep={initialStep}
        steps={[idStep, fieldsStep, overviewStep]}
        onCancel={closeHandler}
        onSave={savePreset}
        onReset={reset}
      />
    </>
  );
};
